import {
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
//
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Badge,
} from '@mui/material';
import { VBMAccordion } from '..';
import VBMBookingSessionsBulkActions from './VBMBookingSessionsBulkActions';
import Iconify from '@/components/Iconify';
// api
import { getFilteredBookingSession } from '@/features/bookings-management/api';
// hooks + utils
import useTabs from '@/hooks/useTabs';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { useGetAccess } from '@/hooks/useAccess';
import useAuth from '@/hooks/useAuth';
import { BookingSession } from '@/features/bookings-management/types';

const FILTER_ALL = 'all';
const FILTER_UNASSIGNED = 'unassigned';

const WEEKDAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
].map((weekday) => ({ value: weekday, label: weekday }));

const getSessionWeekday = (session: BookingSession) => {
  const parsedDate = parseISO(session.date);
  return isValid(parsedDate) ? format(parsedDate, 'EEEE') : '';
};

const getCleanerName = (
  cleaner: BookingSession['booking_session_cleaners'][number]
) => `${cleaner?.first_name ?? 'N/A'} ${cleaner?.last_name ?? 'N/A'}`.trim();

const getStatusOptionValue = (
  statusId: number | undefined,
  status?: BookingSession['booking_session_status']
) => {
  if (statusId) return `id:${statusId}`;
  return `value:${status?.value ?? status?.display ?? 'unknown'}`;
};

const getStatusLabel = (status?: BookingSession['booking_session_status']) =>
  status?.display ?? status?.value ?? 'N/A';

const getSessionSearchText = (session: BookingSession) =>
  [
    session.id,
    session.date,
    getSessionWeekday(session),
    session.time,
    session.booking_session_cleaners.map(getCleanerName).join(' '),
    getStatusLabel(session.booking_session_status),
    session.booking_session_status?.value,
    getStatusLabel(session.booking_session_payment_status),
    session.booking_session_payment_status?.value,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export default function VBMBookingSessions() {
  const { bookingId, sessionFilter } = useParams();
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');
  const { currentTab, onChangeTab, setCurrentTab } = useTabs(sessionFilter);
  const navigate = useNavigate();
  const { errorMsg } = useSnackbarMsg();
  const { canUpdate } = useGetAccess('bookings');
  const { isSuperAdmin } = useAuth();
  const canBulkDelete = isSuperAdmin === true;
  const canUseBulkActions = canUpdate || canBulkDelete;
  const [selectedSessionIds, setSelectedSessionIds] = useState<number[]>([]);
  const [rangeSelectionAnchorId, setRangeSelectionAnchorId] = useState<
    number | null
  >(null);
  const [filterWeekday, setFilterWeekday] = useState(FILTER_ALL);
  const [filterCleaner, setFilterCleaner] = useState(FILTER_ALL);
  const [filterSessionStatus, setFilterSessionStatus] = useState(FILTER_ALL);
  const [filterPaymentStatus, setFilterPaymentStatus] = useState(FILTER_ALL);
  const [filterSearch, setFilterSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    sessionFilter && setCurrentTab(sessionFilter);
  }, [sessionFilter, setCurrentTab]);

  const handleTabChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    onChangeTab(event, newValue);
    setSelectedSessionIds([]);
    setRangeSelectionAnchorId(null);
    navigate(
      bookingsManagementPath('view', {
        bookingId: String(bookingId),
        bookingTabSid: 'booking-sessions',
        sessionFilter: newValue,
      })
    );
  };

  // BOOKING SESSIONS
  const { data: bookingSessions, isLoading: isBookingSessionsDataLoading } =
    useQuery({
      queryKey: ['bookingSessions', sessionFilter],
      queryFn: () =>
        getFilteredBookingSession(Number(bookingId), String(sessionFilter)),
      onError: (error) => {
        errorMsg(error, `Something went wrong while fetching booking data`);
        navigate(bookingsManagementPath('list'));
      },
      refetchOnWindowFocus: false,
      enabled: !!bookingId,
    });

  const noSessions = bookingSessions && bookingSessions.length === 0;
  const cleanerFilterOptions = useMemo(() => {
    const cleanerOptions = new Map<string, string>();

    (bookingSessions ?? []).forEach((session) => {
      session.booking_session_cleaners.forEach((cleaner) => {
        if (cleaner?.id) {
          cleanerOptions.set(`id:${cleaner.id}`, getCleanerName(cleaner));
        }
      });
    });

    return [
      { value: FILTER_ALL, label: 'All cleaners' },
      { value: FILTER_UNASSIGNED, label: 'Unassigned' },
      ...Array.from(cleanerOptions.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    ];
  }, [bookingSessions]);
  const sessionStatusFilterOptions = useMemo(() => {
    const statusOptions = new Map<string, string>();

    (bookingSessions ?? []).forEach((session) => {
      statusOptions.set(
        getStatusOptionValue(session.status_id, session.booking_session_status),
        getStatusLabel(session.booking_session_status)
      );
    });

    return [
      { value: FILTER_ALL, label: 'All session statuses' },
      ...Array.from(statusOptions.entries()).map(([value, label]) => ({
        value,
        label,
      })),
    ];
  }, [bookingSessions]);
  const paymentStatusFilterOptions = useMemo(() => {
    const statusOptions = new Map<string, string>();

    (bookingSessions ?? []).forEach((session) => {
      statusOptions.set(
        getStatusOptionValue(
          session.payment_status_id,
          session.booking_session_payment_status
        ),
        getStatusLabel(session.booking_session_payment_status)
      );
    });

    return [
      { value: FILTER_ALL, label: 'All payment statuses' },
      ...Array.from(statusOptions.entries()).map(([value, label]) => ({
        value,
        label,
      })),
    ];
  }, [bookingSessions]);
  const isFiltering =
    filterWeekday !== FILTER_ALL ||
    filterCleaner !== FILTER_ALL ||
    filterSessionStatus !== FILTER_ALL ||
    filterPaymentStatus !== FILTER_ALL ||
    filterSearch.trim().length > 0;
  const activeFilterCount = [
    filterWeekday !== FILTER_ALL,
    filterCleaner !== FILTER_ALL,
    filterSessionStatus !== FILTER_ALL,
    filterPaymentStatus !== FILTER_ALL,
    filterSearch.trim().length > 0,
  ].filter(Boolean).length;
  const filteredBookingSessions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();

    return (bookingSessions ?? []).filter((session) => {
      const matchesWeekday =
        filterWeekday === FILTER_ALL ||
        getSessionWeekday(session) === filterWeekday;
      const matchesCleaner =
        filterCleaner === FILTER_ALL ||
        (filterCleaner === FILTER_UNASSIGNED
          ? session.booking_session_cleaners.length === 0
          : session.booking_session_cleaners.some(
              (cleaner) => `id:${cleaner.id}` === filterCleaner
            ));
      const matchesSessionStatus =
        filterSessionStatus === FILTER_ALL ||
        getStatusOptionValue(
          session.status_id,
          session.booking_session_status
        ) === filterSessionStatus;
      const matchesPaymentStatus =
        filterPaymentStatus === FILTER_ALL ||
        getStatusOptionValue(
          session.payment_status_id,
          session.booking_session_payment_status
        ) === filterPaymentStatus;
      const matchesSearch =
        normalizedSearch.length === 0 ||
        getSessionSearchText(session).includes(normalizedSearch);

      return (
        matchesWeekday &&
        matchesCleaner &&
        matchesSessionStatus &&
        matchesPaymentStatus &&
        matchesSearch
      );
    });
  }, [
    bookingSessions,
    filterCleaner,
    filterPaymentStatus,
    filterSearch,
    filterSessionStatus,
    filterWeekday,
  ]);
  const visibleSessionIds = useMemo(
    () => filteredBookingSessions.map((session) => session.id),
    [filteredBookingSessions]
  );
  const visibleSessionIdsSet = useMemo(
    () => new Set(visibleSessionIds),
    [visibleSessionIds]
  );
  const selectedSessionIdsSet = useMemo(
    () => new Set(selectedSessionIds),
    [selectedSessionIds]
  );
  const selectedSessions = useMemo(
    () =>
      filteredBookingSessions.filter((session) =>
        selectedSessionIdsSet.has(session.id)
      ),
    [filteredBookingSessions, selectedSessionIdsSet]
  );

  const handleToggleSession = useCallback(
    (
      bookingSessionId: number,
      event?: Pick<MouseEvent<HTMLElement>, 'shiftKey'>
    ) => {
      const anchorIndex =
        rangeSelectionAnchorId === null
          ? -1
          : visibleSessionIds.indexOf(rangeSelectionAnchorId);
      const selectedIndex = visibleSessionIds.indexOf(bookingSessionId);
      const shouldSelectRange =
        !!event?.shiftKey && anchorIndex !== -1 && selectedIndex !== -1;

      setSelectedSessionIds((currentSelectedIds) => {
        if (shouldSelectRange) {
          const [startIndex, endIndex] =
            anchorIndex < selectedIndex
              ? [anchorIndex, selectedIndex]
              : [selectedIndex, anchorIndex];
          const rangeSessionIds = visibleSessionIds.slice(
            startIndex,
            endIndex + 1
          );
          const nextSelectedIds = new Set(currentSelectedIds);
          rangeSessionIds.forEach((id) => nextSelectedIds.add(id));

          return visibleSessionIds.filter((id) => nextSelectedIds.has(id));
        }

        return currentSelectedIds.includes(bookingSessionId)
          ? currentSelectedIds.filter((id) => id !== bookingSessionId)
          : [...currentSelectedIds, bookingSessionId];
      });

      if (!shouldSelectRange) {
        setRangeSelectionAnchorId(bookingSessionId);
      }
    },
    [rangeSelectionAnchorId, visibleSessionIds]
  );

  const handleSelectAllSessions = useCallback(
    (checked: boolean) => {
      setSelectedSessionIds(checked ? visibleSessionIds : []);
      setRangeSelectionAnchorId(null);
    },
    [visibleSessionIds]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedSessionIds([]);
    setRangeSelectionAnchorId(null);
  }, []);

  const handleSelectionChange = useCallback((selectedIds: number[]) => {
    setSelectedSessionIds(selectedIds);
    setRangeSelectionAnchorId(null);
  }, []);

  useEffect(() => {
    setSelectedSessionIds((currentSelectedIds) =>
      currentSelectedIds.filter((id) => visibleSessionIdsSet.has(id))
    );
    setRangeSelectionAnchorId((currentAnchorId) =>
      currentAnchorId !== null && visibleSessionIdsSet.has(currentAnchorId)
        ? currentAnchorId
        : null
    );
  }, [visibleSessionIdsSet]);

  const resetFilters = () => {
    setFilterWeekday(FILTER_ALL);
    setFilterCleaner(FILTER_ALL);
    setFilterSessionStatus(FILTER_ALL);
    setFilterPaymentStatus(FILTER_ALL);
    setFilterSearch('');
  };

  const CLEANING_SESSION_TABS = [
    {
      value: 'previous',
      label: 'Previous',
    },
    {
      value: 'today',
      label: 'Today',
    },
    {
      value: 'upcoming',
      label: 'Upcoming',
    },
  ];
  return (
    <Stack spacing={2} sx={{ mt: -2 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={handleTabChange}
        >
          {CLEANING_SESSION_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              disableRipple
              sx={{ width: 120 }}
            />
          ))}
        </Tabs>
      </Stack>

      <Box sx={{ px: 2 }}>
        {noSessions ? (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
            }}
          >
            There are no sessions available with the following filter:
            <span style={{ fontWeight: 600, paddingLeft: 4 }}>
              {sessionFilter}
            </span>
          </Box>
        ) : (
          <>
            {!bookingSessions || isBookingSessionsDataLoading ? (
              <Stack spacing={1}>
                {[...new Array(5)].map((_, index) => (
                  <Skeleton key={index} variant="rounded" height={50} />
                ))}
              </Stack>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: showFilters ? 1 : 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {isFiltering
                      ? `${filteredBookingSessions.length} of ${
                          bookingSessions.length
                        } ${
                          bookingSessions.length === 1 ? 'session' : 'sessions'
                        }`
                      : `${bookingSessions.length} ${
                          bookingSessions.length === 1 ? 'session' : 'sessions'
                        }`}
                  </Typography>
                  <Tooltip
                    title={showFilters ? 'Hide filters' : 'Show filters'}
                  >
                    <IconButton
                      color={showFilters || isFiltering ? 'primary' : 'inherit'}
                      aria-label={
                        showFilters
                          ? 'Hide session filters'
                          : 'Show session filters'
                      }
                      onClick={() => setShowFilters((current) => !current)}
                    >
                      <Badge
                        color="primary"
                        badgeContent={activeFilterCount}
                        invisible={!isFiltering}
                      >
                        <Iconify icon="lets-icons:filter" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </Stack>
                {showFilters && (
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                      p: 1.25,
                      mb: 1.5,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Day"
                      value={filterWeekday}
                      onChange={(event) => setFilterWeekday(event.target.value)}
                      sx={{ minWidth: { md: 140 } }}
                    >
                      <MenuItem value={FILTER_ALL}>All days</MenuItem>
                      {WEEKDAY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Cleaner"
                      value={filterCleaner}
                      onChange={(event) => setFilterCleaner(event.target.value)}
                      sx={{ minWidth: { md: 180 } }}
                    >
                      {cleanerFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Session status"
                      value={filterSessionStatus}
                      onChange={(event) =>
                        setFilterSessionStatus(event.target.value)
                      }
                      sx={{ minWidth: { md: 180 } }}
                    >
                      {sessionStatusFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Payment status"
                      value={filterPaymentStatus}
                      onChange={(event) =>
                        setFilterPaymentStatus(event.target.value)
                      }
                      sx={{ minWidth: { md: 180 } }}
                    >
                      {paymentStatusFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      placeholder="Search sessions..."
                      value={filterSearch}
                      onChange={(event) => setFilterSearch(event.target.value)}
                      sx={{ flexGrow: 1, minWidth: { md: 220 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify
                              icon="eva:search-fill"
                              sx={{
                                color: 'text.disabled',
                                width: 20,
                                height: 20,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {isFiltering && (
                      <Tooltip title="Reset filters">
                        <IconButton
                          color="inherit"
                          aria-label="Reset session filters"
                          onClick={resetFilters}
                        >
                          <Iconify icon="eva:close-circle-outline" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
                {canUseBulkActions && selectedSessions.length > 0 && (
                  <VBMBookingSessionsBulkActions
                    selectedSessions={selectedSessions}
                    onClearSelection={handleClearSelection}
                    onSelectionChange={handleSelectionChange}
                    visibleSessionCount={filteredBookingSessions.length}
                    onSelectAllSessions={handleSelectAllSessions}
                    canUpdate={canUpdate}
                    canDelete={canBulkDelete}
                  />
                )}
                {filteredBookingSessions.length === 0 ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No sessions match the selected filters.
                    </Typography>
                  </Box>
                ) : (
                  filteredBookingSessions.map((session) => (
                    <VBMAccordion
                      key={session.id}
                      bookingSession={session}
                      isExpanded={Number(sessionID) === session.id}
                      selected={selectedSessionIdsSet.has(session.id)}
                      showSelection={canUseBulkActions}
                      onSelect={handleToggleSession}
                    />
                  ))
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
